<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AutomationHeader from './components/AutomationHeader.vue';
import ExecutionHistory from './components/ExecutionHistory.vue';
import FlowCanvas from './components/FlowCanvas.vue';
import FlowToolbar from './components/FlowToolbar.vue';
import NodeEditor from './components/editors/NodeEditor.vue';
import Modal from 'dashboard/components/Modal.vue';
import pipelineAutomationsAPI from 'dashboard/api/pipelineAutomations';
import { initializeFlow, exportFlow } from './utils/flowHelpers';
import {
  normalizeAutomationFlow,
  normalizeTriggerSubtype,
} from './utils/triggerFilterContract';

const props = defineProps({
  automationId: {
    type: [String, Number],
    default: null,
  },
  // Aba inicial ao montar o editor. 'history' permite abrir direto no
  // histórico de execuções a partir da listagem (botão "Ver histórico").
  initialTab: {
    type: String,
    default: 'canvas',
    validator: v => ['canvas', 'history'].includes(v),
  },
  readonly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'save']);
const store = useStore();
const { t } = useI18n();
const { showAlert } = useAlert();

// Estado local
const isLoading = ref(false);
const loadError = ref(false);
const isSaving = ref(false);
// Honra a aba inicial pedida pelo pai. 'history' só faz sentido com uma
// automação salva; o guard de render (activeTab === 'history' && automationId)
// e o :disabled do botão garantem coerência se vier sem id.
const activeTab = ref(
  (props.readonly || props.initialTab === 'history') && props.automationId
    ? 'history'
    : 'canvas'
);
const automationName = ref(t('PIPELINE.FLOW_BUILDER.NEW_AUTOMATION_NAME'));
const automationDescription = ref('');
const isActive = ref(true);

// Run-control state (declared early so the global Esc handler can dismiss
// these overlays). Behaviour lives in the "Run controls" section below.
const isTesting = ref(false);
const isExecuting = ref(false);
const showExecuteConfirm = ref(false);
const showRunModal = ref(false);
const runResult = ref(null);

// ── Dirty-state tracking ───────────────────────────────────────────────────
// Compares a snapshot taken at load (or after a successful save) against the
// current editable state. JSON-serializing the flow makes the comparison
// resilient to Vue proxies and reference equality false positives.
const initialSnapshot = ref(null);

const captureSnapshot = () => {
  const flowData = exportFlow(store);
  return JSON.stringify({
    name: automationName.value,
    description: automationDescription.value,
    active: isActive.value,
    flow: flowData,
  });
};

const isDirty = computed(() => {
  if (isLoading.value) return false;
  if (initialSnapshot.value === null) return false;
  return captureSnapshot() !== initialSnapshot.value;
});

// Sidebar editor state
const editingNodeId = ref(null);
const nodeEditorRef = ref(null);
const showNodeEditor = computed({
  get: () => Boolean(editingNodeId.value),
  set: isOpen => {
    if (!isOpen) editingNodeId.value = null;
  },
});
const openNodeEditor = nodeId => {
  if (props.readonly) return;
  editingNodeId.value = nodeId || null;
};
const closeNodeEditor = () => {
  editingNodeId.value = null;
};
const requestNodeEditorClose = () =>
  nodeEditorRef.value?.requestClose?.() ?? true;

// ── Flow summary (banner se-então) ───────────────────────────────────────
// Resumo natural-language reativo do flow atual. Mostra ao usuário leigo
// o que o flow VAI FAZER em português, sem precisar entender canvas/nodes.
const flowNodes = computed(() => store.state.automationFlow?.flow?.nodes || []);

const summaryTriggerLabel = computed(() => {
  const trigger = flowNodes.value.find(n => n.type === 'trigger');
  if (!trigger) return t('PIPELINE.FLOW_BUILDER.SUMMARY.NO_TRIGGER');
  const subtype = normalizeTriggerSubtype(trigger.data);
  if (!subtype) return t('PIPELINE.FLOW_BUILDER.SUMMARY.NO_TRIGGER');
  // Lookup label das chaves de toolbar (já existem traduções)
  const key = `PIPELINE.FLOW_BUILDER.NODE.TRIGGER.${subtype}`;
  const translated = t(key);
  return translated !== key ? translated : subtype;
});

const summaryActionCount = computed(
  () => flowNodes.value.filter(n => n.type === 'action').length
);

const summaryActionsLabel = computed(() => {
  if (summaryActionCount.value === 0) {
    return t('PIPELINE.FLOW_BUILDER.SUMMARY.NO_ACTIONS');
  }
  return t(
    'PIPELINE.FLOW_BUILDER.SUMMARY.ACTION_COUNT',
    summaryActionCount.value,
    {
      count: summaryActionCount.value,
    }
  );
});

// Modal para confirmar nome
const showSaveModal = ref(false);
const modalName = ref('');

// Pre-save validation: surface server-side flow validator errors BEFORE
// hitting create/update. Avoids the "saved a broken flow that explodes
// at first execution" pattern. Network errors here are non-fatal — we
// fall through to the save and let server-side validation reject if
// the flow is truly invalid.
const runPreSaveValidation = async flowData => {
  try {
    const { data } = await pipelineAutomationsAPI.validateFlow(flowData);
    return {
      valid: data?.valid !== false,
      errors: data?.errors || [],
      warnings: data?.warnings || [],
    };
  } catch (err) {
    const data = err?.response?.data;
    if (data && data.valid === false) {
      return {
        valid: false,
        errors: data.errors || [],
        warnings: data.warnings || [],
      };
    }
    // Network or unexpected error — degrade gracefully (don't block save).
    return { valid: true, errors: [], warnings: [], networkFailed: true };
  }
};

// Salvar automação
const saveAutomation = async () => {
  if (props.readonly) return;
  isSaving.value = true;
  try {
    const flowData = normalizeAutomationFlow(exportFlow(store));

    // Defesa: bloqueia save com flow visualmente vazio. Evita gravar lixo
    // por engano (clique em Ctrl+S antes do mount completar, p.ex.).
    if (!flowData.nodes || flowData.nodes.length === 0) {
      showAlert(t('PIPELINE.FLOW_BUILDER.SAVE_EMPTY_FLOW_ERROR'));
      return;
    }

    // Pre-save validation — surface errors before the round-trip to create/update.
    const validation = await runPreSaveValidation(flowData);
    if (!validation.valid && validation.errors.length > 0) {
      // Show the first error verbatim. Subsequent errors are still in the
      // payload if we want to render them in a list view later.
      const firstError = validation.errors[0];
      const errorMessage =
        typeof firstError === 'string'
          ? firstError
          : firstError?.message || JSON.stringify(firstError);
      showAlert(
        `${t('PIPELINE.FLOW_BUILDER.VALIDATION_FAILED_PREFIX')}: ${errorMessage}`
      );
      return;
    }

    const triggerNode = flowData.nodes.find(n => n.type === 'trigger') || null;
    const triggerType = (() => {
      const sub =
        triggerNode?.data?.type || triggerNode?.data?.event_type || '';
      if (sub === 'scheduled') return 'scheduled';
      // The builder persists the webhook subtype as 'webhook_received'.
      if (sub === 'webhook' || sub === 'webhook_received') return 'webhook';
      if (sub === 'manual') return 'manual';
      return 'event';
    })();

    const automationData = {
      id: props.automationId,
      name: automationName.value,
      description: automationDescription.value,
      active: isActive.value,
      flow: flowData,
      trigger_type: triggerType,
    };

    const saved = await store.dispatch(
      'automationFlow/saveAutomation',
      automationData
    );
    // Keep currentAutomation in sync with the persisted safe representation so
    // node editors can react to server-generated state such as
    // webhook_configured. Credentials are fetched separately by administrators.
    if (saved) {
      store.commit('automationFlow/SET_CURRENT_AUTOMATION', saved);
    }
    showAlert(t('PIPELINE.FLOW_BUILDER.SAVE_SUCCESS'));
    // Reset dirty baseline so the user can immediately close without prompt.
    initialSnapshot.value = captureSnapshot();
    // Propaga o id retornado para que o pai mantenha o editor aberto na
    // automação recém-criada (em vez de fechar e o usuário precisar abrir
    // de novo pela lista).
    emit('save', saved?.id ?? props.automationId);
  } catch {
    showAlert(t('PIPELINE.AUTOMATIONS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

// Quando clicar em salvar, mostrar modal para confirmar o nome (só para nova automação)
const handleSaveClick = () => {
  if (props.readonly) return;
  if (!props.automationId) {
    // Nova automação - mostrar modal
    modalName.value = automationName.value;
    showSaveModal.value = true;
  } else {
    // Editando automação existente - salvar diretamente
    saveAutomation();
  }
};

// Confirmar salvamento do modal
const confirmSave = () => {
  automationName.value = modalName.value;
  showSaveModal.value = false;
  saveAutomation();
};

// Cancelar modal
const cancelModal = () => {
  showSaveModal.value = false;
};

// Cancelar edição — Modal customizado (substitui window.confirm nativo
// que quebrava i18n e estética). Só prompta se há mudanças não salvas;
// caso contrário fecha direto (era falso positivo abrir o modal toda vez).
const showCancelConfirm = ref(false);
let pendingRouteLeave = null;
const cancelEditing = () => {
  if (!isDirty.value) {
    emit('close');
    return;
  }
  showCancelConfirm.value = true;
};
const confirmCancel = () => {
  showCancelConfirm.value = false;
  // Reset baseline so the route leave guard and beforeunload don't fire
  // for the very same close we just confirmed.
  initialSnapshot.value = captureSnapshot();
  if (pendingRouteLeave) {
    const continueNavigation = pendingRouteLeave;
    pendingRouteLeave = null;
    continueNavigation(true);
    return;
  }
  emit('close');
};
const dismissCancel = () => {
  showCancelConfirm.value = false;
  if (pendingRouteLeave) {
    const cancelNavigation = pendingRouteLeave;
    pendingRouteLeave = null;
    cancelNavigation(false);
  }
};

// Global keyboard shortcuts: Esc closes the topmost overlay, Ctrl+S saves.
const handleGlobalKeydown = event => {
  if (event.key === 'Escape') {
    // Close in priority order so Esc dismisses one layer at a time.
    if (showRunModal.value) {
      showRunModal.value = false;
      return;
    }
    if (showExecuteConfirm.value) {
      showExecuteConfirm.value = false;
      return;
    }
    // The shared Modal owns Escape for the node editor and runs its async
    // beforeClose guard. Mutating editingNodeId here would bypass that guard.
    if (editingNodeId.value) return;
    return;
  }
  // Ctrl+S — skip when focused inside an input or textarea
  if (!event.ctrlKey || event.key !== 's') return;
  if (props.readonly) return;
  const tag = event.target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA') return;
  event.preventDefault();
  handleSaveClick();
};

// Browser tab/window close guard — only triggers when there are unsaved
// changes. Browsers ignore the custom message (security), but the prompt
// itself blocks accidental closes.
const handleBeforeUnload = event => {
  if (!isDirty.value) return undefined;
  event.preventDefault();
  // Required for legacy browsers to honor the prompt.
  // eslint-disable-next-line no-param-reassign
  event.returnValue = '';
  return '';
};

// ── Run controls: dry-run (safe preview) + real execute ───────────────────
// dryRun simula a execução sem efeitos colaterais e devolve os passos; execute
// dispara as ações reais (por isso pede confirmação). O resultado dos dois é
// mostrado no mesmo painel (runResult), distinguindo pelo campo `mode`.
// (state refs declared near the top so the Esc handler can reference them)
const runDryRun = async () => {
  if (props.readonly || !props.automationId || isTesting.value) return;
  isTesting.value = true;
  try {
    const { data } = await pipelineAutomationsAPI.dryRun(
      props.automationId,
      {}
    );
    runResult.value = { mode: 'dry_run', ...data };
    showRunModal.value = true;
  } catch (e) {
    // A simulação devolve 422 quando o flow é inválido — o corpo ainda traz
    // steps/erro, então mostramos o painel em vez de um toast genérico.
    const data = e?.response?.data;
    if (data) {
      runResult.value = { mode: 'dry_run', ...data };
      showRunModal.value = true;
    } else {
      showAlert(t('PIPELINE.FLOW_BUILDER.RUN.DRY_RUN_ERROR'));
    }
  } finally {
    isTesting.value = false;
  }
};

const confirmExecute = () => {
  if (props.readonly || !props.automationId) return;
  showExecuteConfirm.value = true;
};
const dismissExecuteConfirm = () => {
  showExecuteConfirm.value = false;
};

const runExecute = async () => {
  showExecuteConfirm.value = false;
  if (props.readonly || !props.automationId || isExecuting.value) return;
  isExecuting.value = true;
  try {
    const { data } = await pipelineAutomationsAPI.execute(
      props.automationId,
      {}
    );
    runResult.value = { mode: 'execute', ...data };
    showRunModal.value = true;
    showAlert(
      data?.success === false
        ? t('PIPELINE.FLOW_BUILDER.RUN.EXECUTE_FAILED')
        : t('PIPELINE.FLOW_BUILDER.RUN.EXECUTE_SUCCESS')
    );
  } catch (e) {
    // Execução real devolve 422 em falha com { success:false, error }. Mostra
    // o detalhe no painel para o usuário saber o PORQUÊ da falha.
    const data = e?.response?.data;
    if (data) {
      runResult.value = { mode: 'execute', ...data };
      showRunModal.value = true;
      showAlert(t('PIPELINE.FLOW_BUILDER.RUN.EXECUTE_FAILED'));
    } else {
      showAlert(t('PIPELINE.FLOW_BUILDER.RUN.EXECUTE_ERROR'));
    }
  } finally {
    isExecuting.value = false;
  }
};

const closeRunModal = () => {
  showRunModal.value = false;
};

// Normaliza os passos do dry-run para render. O backend devolve
// steps:[{order,node_id,node_type,label,would_execute[],result}].
const runSteps = computed(() => {
  const steps = runResult.value?.steps;
  return Array.isArray(steps) ? steps : [];
});

const runHasError = computed(() => {
  const r = runResult.value;
  if (!r) return false;
  // failed_node_id covers the "completed but a node errored" case where
  // success stays true but a step still failed.
  return (
    r.success === false || !!r.error || !!r.error_message || !!r.failed_node_id
  );
});

const runErrorText = computed(
  () =>
    runResult.value?.error ||
    runResult.value?.error_message ||
    runResult.value?.failed_node_error ||
    ''
);

// Human label for the node that failed, resolved against the loaded flow.
const runFailedNodeLabel = computed(() => {
  const id = runResult.value?.failed_node_id;
  if (!id) return '';
  const node = flowNodes.value.find(n => n.id === id);
  return node?.data?.label || node?.data?.type || id;
});

const initializeBuilder = async () => {
  isLoading.value = true;
  loadError.value = false;
  try {
    if (props.automationId) {
      // Carregamento de automação existente...
      await store.dispatch(
        'automationFlow/fetchAutomation',
        props.automationId
      );
      const currentAutomation =
        store.getters['automationFlow/getCurrentAutomation'];
      const automation =
        String(currentAutomation?.id) === String(props.automationId)
          ? currentAutomation
          : store.getters['automationFlow/getAutomationById'](
              props.automationId
            );

      if (!automation) throw new Error('automation_not_loaded');

      automationName.value = automation.name;
      automationDescription.value = automation.description || '';
      isActive.value = !!automation.active;

      // Inicializar o flow com os dados existentes
      initializeFlow(store, automation.flow || {});
    } else {
      // Inicializar uma nova automação
      initializeFlow(store); // Função que configura um novo fluxo com nó de gatilho padrão
    }
    // Capture pristine baseline only after a complete initialization.
    initialSnapshot.value = captureSnapshot();
  } catch {
    loadError.value = true;
    showAlert(t('PIPELINE.AUTOMATIONS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

// Verificações de inicialização
onMounted(() => {
  document.addEventListener('keydown', handleGlobalKeydown);
  window.addEventListener('beforeunload', handleBeforeUnload);
  initializeBuilder();
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleGlobalKeydown);
  window.removeEventListener('beforeunload', handleBeforeUnload);
});

const guardUnsavedNavigation = (_to, _from, next) => {
  if (!isDirty.value) {
    next();
    return;
  }
  pendingRouteLeave = next;
  showCancelConfirm.value = true;
};

onBeforeRouteLeave(guardUnsavedNavigation);
onBeforeRouteUpdate(guardUnsavedNavigation);
</script>

<template>
  <div class="flex w-full flex-1 min-w-0 flex-col h-full bg-n-background">
    <AutomationHeader
      v-model:name="automationName"
      v-model:active="isActive"
      :is-saving="isSaving"
      :automation-id="props.automationId"
      :readonly="props.readonly"
      @save="handleSaveClick"
      @cancel="cancelEditing"
    />

    <!-- Tab navigation -->
    <div
      v-if="!loadError"
      role="tablist"
      class="flex flex-wrap items-center gap-1 border-b border-n-weak bg-n-solid-2 px-3 py-1 sm:px-4"
    >
      <button
        v-if="!readonly"
        role="tab"
        :aria-selected="activeTab === 'canvas' ? 'true' : 'false'"
        class="min-h-11 rounded-t border-b-2 px-3 py-2.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
        :class="[
          activeTab === 'canvas'
            ? 'border-n-brand text-n-slate-12'
            : 'border-transparent text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1',
        ]"
        @click="activeTab = 'canvas'"
      >
        {{ t('PIPELINE.FLOW_BUILDER.TAB_CANVAS') }}
      </button>
      <button
        role="tab"
        :aria-selected="activeTab === 'history' ? 'true' : 'false'"
        class="min-h-11 rounded-t border-b-2 px-3 py-2.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:cursor-not-allowed disabled:opacity-40"
        :class="[
          activeTab === 'history'
            ? 'border-n-brand text-n-slate-12'
            : 'border-transparent text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1',
        ]"
        :disabled="!automationId"
        :title="
          !automationId ? t('PIPELINE.FLOW_BUILDER.TAB_HISTORY_DISABLED') : ''
        "
        @click="activeTab = 'history'"
      >
        {{ t('PIPELINE.FLOW_BUILDER.TAB_HISTORY') }}
      </button>

      <!-- Run controls (right-aligned): dry-run (safe preview) + real execute -->
      <div
        v-if="!readonly"
        class="flex w-full flex-wrap items-center gap-2 py-1 sm:ml-auto sm:w-auto"
      >
        <button
          type="button"
          class="inline-flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-md bg-n-alpha-1 px-3 py-1.5 text-xs font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:cursor-not-allowed disabled:opacity-40 sm:flex-none"
          :disabled="!automationId || isTesting"
          :title="
            !automationId ? t('PIPELINE.FLOW_BUILDER.TAB_HISTORY_DISABLED') : ''
          "
          @click="runDryRun"
        >
          <span
            :class="
              isTesting
                ? 'i-lucide-loader-2 animate-spin'
                : 'i-lucide-flask-conical'
            "
            class="size-3.5"
            aria-hidden="true"
          />
          {{ t('PIPELINE.FLOW_BUILDER.RUN.TEST') }}
        </button>
        <button
          type="button"
          class="inline-flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-md bg-n-brand px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40 sm:flex-none"
          :disabled="!automationId || isExecuting"
          :title="
            !automationId ? t('PIPELINE.FLOW_BUILDER.TAB_HISTORY_DISABLED') : ''
          "
          @click="confirmExecute"
        >
          <span
            :class="
              isExecuting ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-play'
            "
            class="size-3.5"
            aria-hidden="true"
          />
          {{ t('PIPELINE.FLOW_BUILDER.RUN.EXECUTE') }}
        </button>
      </div>
    </div>

    <!-- Banner se-então: resumo natural-language do que o flow VAI FAZER -->
    <div
      v-if="!loadError && activeTab === 'canvas'"
      class="flex flex-wrap items-center gap-2 border-b border-n-weak bg-n-alpha-1/40 px-4 py-2 text-xs sm:gap-3"
      role="region"
      :aria-label="t('PIPELINE.FLOW_BUILDER.SUMMARY.BANNER_LABEL')"
    >
      <span
        class="i-lucide-zap size-3.5 text-n-amber-11 shrink-0"
        aria-hidden="true"
      />
      <span class="text-n-slate-10 font-semibold uppercase tracking-wider">
        {{ t('PIPELINE.FLOW_BUILDER.SUMMARY.WHEN') }}
      </span>
      <span class="min-w-0 text-n-slate-12 font-medium break-words">
        {{ summaryTriggerLabel }}
      </span>
      <span
        class="i-lucide-arrow-right size-3 text-n-slate-9 shrink-0"
        aria-hidden="true"
      />
      <span class="text-n-slate-10 font-semibold uppercase tracking-wider">
        {{ t('PIPELINE.FLOW_BUILDER.SUMMARY.THEN') }}
      </span>
      <span class="min-w-0 text-n-slate-12 font-medium break-words">
        {{ summaryActionsLabel }}
      </span>
    </div>

    <div class="flex flex-grow flex-col overflow-hidden lg:flex-row">
      <div
        v-if="loadError"
        role="alert"
        data-testid="automation-load-error"
        class="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center"
      >
        <span
          class="i-lucide-circle-alert size-10 text-n-ruby-9"
          aria-hidden="true"
        />
        <div class="max-w-md">
          <h2 class="text-heading-3 text-n-slate-12">
            {{ t('PIPELINE.AUTOMATIONS.LOAD_ERROR_TITLE') }}
          </h2>
          <p class="mt-2 text-sm text-n-slate-11">
            {{ t('PIPELINE.AUTOMATIONS.LOAD_ERROR') }}
          </p>
        </div>
        <button
          type="button"
          class="min-h-11 rounded-md bg-n-brand px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2"
          @click="initializeBuilder"
        >
          {{ t('PIPELINE.AUTOMATIONS.RETRY') }}
        </button>
      </div>

      <template v-else-if="activeTab === 'canvas'">
        <div class="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
          <FlowCanvas :is-loading="isLoading" @edit-node="openNodeEditor" />
          <FlowToolbar />
        </div>

        <Modal
          v-model:show="showNodeEditor"
          modal-type="right-aligned"
          :show-close-button="false"
          :aria-label="t('PIPELINE.FLOW_BUILDER.NODE_EDITOR_REGION')"
          :before-close="requestNodeEditorClose"
          :on-close="closeNodeEditor"
        >
          <div class="flex h-full min-h-0 w-full flex-col overflow-hidden">
            <NodeEditor
              v-if="editingNodeId"
              ref="nodeEditorRef"
              :node-id="editingNodeId"
              @close="closeNodeEditor"
              @save="closeNodeEditor"
            />
          </div>
        </Modal>
      </template>
      <ExecutionHistory
        v-else-if="activeTab === 'history' && automationId"
        :automation-id="automationId"
        class="flex-1"
      />
      <div
        v-else-if="activeTab === 'history' && !automationId"
        class="flex-1 flex flex-col items-center justify-center gap-4 text-center p-8"
      >
        <span class="i-lucide-save text-4xl text-n-slate-9" />
        <p class="text-sm text-n-slate-10">
          {{ t('PIPELINE.FLOW_BUILDER.SAVE_FIRST_MESSAGE') }}
        </p>
        <button
          type="button"
          class="min-h-11 rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-n-brand/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2"
          @click="handleSaveClick"
        >
          {{ t('PIPELINE.FLOW_BUILDER.SAVE_NOW') }}
        </button>
      </div>
    </div>

    <!-- Modal para confirmar nome da automação -->
    <Modal
      v-model:show="showSaveModal"
      :show-close-button="false"
      :aria-label="t('PIPELINE.AUTOMATIONS.SAVE_MODAL.TITLE')"
      initial-focus-selector="#flow-automation-name"
      :on-close="cancelModal"
    >
      <div
        class="w-full rounded-xl bg-n-solid-2 p-6 shadow-xl outline outline-1 outline-n-weak"
      >
        <h3 class="text-heading-3 text-n-slate-12 mb-2">
          {{ t('PIPELINE.AUTOMATIONS.SAVE_MODAL.TITLE') }}
        </h3>
        <p class="text-label-small text-n-slate-11 mb-4">
          {{ t('PIPELINE.AUTOMATIONS.SAVE_MODAL.DESCRIPTION') }}
        </p>
        <label
          for="flow-automation-name"
          class="mb-1.5 block text-xs font-medium text-n-slate-11"
        >
          {{ t('PIPELINE.AUTOMATIONS.SAVE_MODAL.NAME_LABEL') }}
        </label>
        <input
          id="flow-automation-name"
          v-model="modalName"
          name="flow-automation-name"
          type="text"
          autocomplete="off"
          class="mb-3 min-h-11 w-full rounded-md border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-n-brand"
          :placeholder="t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.NAME_PLACEHOLDER')"
          @keyup.enter="confirmSave"
        />
        <label
          for="flow-automation-description"
          class="mb-1.5 block text-xs font-medium text-n-slate-11"
        >
          {{ t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.DESCRIPTION_LABEL') }}
        </label>
        <textarea
          id="flow-automation-description"
          v-model="automationDescription"
          name="flow-automation-description"
          rows="2"
          class="w-full px-3 py-2 border border-n-weak rounded-md bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent mb-4 text-sm resize-none"
          :placeholder="
            t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.DESCRIPTION_PLACEHOLDER')
          "
        />
        <div class="flex justify-end gap-3">
          <button
            type="button"
            class="min-h-11 px-4 py-2 rounded-md bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 transition-colors text-sm font-medium"
            @click="cancelModal"
          >
            {{ t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.CANCEL') }}
          </button>
          <button
            type="button"
            class="min-h-11 px-4 py-2 rounded-md bg-n-brand text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            :disabled="!modalName.trim()"
            @click="confirmSave"
          >
            {{ t('PIPELINE.AUTOMATIONS.FLOW_EDITOR.SAVE') }}
          </button>
        </div>
      </div>
    </Modal>

    <!-- Modal de confirmação de cancelamento (substitui window.confirm) -->
    <Modal
      v-model:show="showCancelConfirm"
      :show-close-button="false"
      :aria-label="t('PIPELINE.FLOW_BUILDER.CANCEL_TITLE')"
      :on-close="dismissCancel"
    >
      <div
        class="w-full rounded-xl bg-n-solid-2 p-6 shadow-xl outline outline-1 outline-n-weak"
      >
        <h3 class="text-heading-3 text-n-slate-12 mb-2">
          {{ t('PIPELINE.FLOW_BUILDER.CANCEL_TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 mb-4">
          {{ t('PIPELINE.FLOW_BUILDER.CANCEL_CONFIRM') }}
        </p>
        <div class="flex justify-end gap-3">
          <button
            type="button"
            class="min-h-11 px-4 py-2 rounded-md bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 transition-colors text-sm font-medium focus:outline-none focus:ring-2 focus:ring-n-slate-6"
            @click="dismissCancel"
          >
            {{ t('PIPELINE.FLOW_BUILDER.CANCEL_KEEP_EDITING') }}
          </button>
          <button
            type="button"
            class="min-h-11 px-4 py-2 rounded-md bg-n-ruby-9 text-white hover:bg-n-ruby-10 transition-colors text-sm font-medium focus:outline-none focus:ring-2 focus:ring-n-ruby-6"
            @click="confirmCancel"
          >
            {{ t('PIPELINE.FLOW_BUILDER.CANCEL_DISCARD') }}
          </button>
        </div>
      </div>
    </Modal>

    <!-- Modal de confirmação de execução REAL (dispara ações de verdade) -->
    <Modal
      v-model:show="showExecuteConfirm"
      :show-close-button="false"
      :aria-label="t('PIPELINE.FLOW_BUILDER.RUN.CONFIRM_TITLE')"
      :on-close="dismissExecuteConfirm"
    >
      <div
        class="w-full rounded-xl bg-n-solid-2 p-6 shadow-xl outline outline-1 outline-n-weak"
      >
        <div class="flex items-center gap-2 mb-2">
          <span
            class="i-lucide-alert-triangle size-5 text-n-amber-9"
            aria-hidden="true"
          />
          <h3 class="text-heading-3 text-n-slate-12">
            {{ t('PIPELINE.FLOW_BUILDER.RUN.CONFIRM_TITLE') }}
          </h3>
        </div>
        <p class="text-sm text-n-slate-11 mb-4">
          {{ t('PIPELINE.FLOW_BUILDER.RUN.CONFIRM_BODY') }}
        </p>
        <div class="flex justify-end gap-3">
          <button
            type="button"
            class="min-h-11 px-4 py-2 rounded-md bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 transition-colors text-sm font-medium focus:outline-none focus:ring-2 focus:ring-n-slate-6"
            @click="showExecuteConfirm = false"
          >
            {{ t('PIPELINE.FLOW_BUILDER.EDITOR.CANCEL') }}
          </button>
          <button
            type="button"
            class="min-h-11 px-4 py-2 rounded-md bg-n-brand text-white hover:opacity-90 transition-opacity text-sm font-medium focus:outline-none focus:ring-2 focus:ring-n-brand"
            @click="runExecute"
          >
            {{ t('PIPELINE.FLOW_BUILDER.RUN.CONFIRM_EXECUTE') }}
          </button>
        </div>
      </div>
    </Modal>

    <!-- Painel de resultado: simulação (dry-run) OU execução real -->
    <Modal
      v-model:show="showRunModal"
      size="medium"
      :show-close-button="false"
      :aria-label="t('PIPELINE.FLOW_BUILDER.RUN.CLOSE')"
      :on-close="closeRunModal"
    >
      <div
        class="flex max-h-[80vh] w-full flex-col rounded-xl bg-n-solid-2 shadow-xl outline outline-1 outline-n-weak"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between px-5 py-3 border-b border-n-weak flex-shrink-0"
        >
          <div class="flex items-center gap-2">
            <span
              :class="
                runResult?.mode === 'dry_run'
                  ? 'i-lucide-flask-conical text-n-slate-11'
                  : runHasError
                    ? 'i-lucide-x-circle text-n-ruby-9'
                    : 'i-lucide-check-circle text-n-teal-9'
              "
              class="size-5"
              aria-hidden="true"
            />
            <h3 class="text-heading-3 text-n-slate-12">
              {{
                runResult?.mode === 'dry_run'
                  ? t('PIPELINE.FLOW_BUILDER.RUN.RESULT_TITLE_DRY')
                  : t('PIPELINE.FLOW_BUILDER.RUN.RESULT_TITLE_EXEC')
              }}
            </h3>
          </div>
          <button
            type="button"
            class="flex min-h-11 min-w-11 items-center justify-center rounded text-n-slate-10 hover:bg-n-alpha-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            :aria-label="t('PIPELINE.FLOW_BUILDER.EDITOR.CANCEL')"
            @click="closeRunModal"
          >
            <span class="i-lucide-x size-4" aria-hidden="true" />
          </button>
        </div>

        <!-- Body -->
        <div class="px-5 py-4 overflow-y-auto flex-1 space-y-3">
          <!-- Dry-run banner -->
          <p
            v-if="runResult?.mode === 'dry_run'"
            class="text-xs text-n-slate-11 bg-n-alpha-1 rounded-lg px-3 py-2 flex items-center gap-2"
          >
            <span class="i-lucide-info size-3.5 shrink-0" aria-hidden="true" />
            {{ t('PIPELINE.FLOW_BUILDER.RUN.DRY_RUN_HINT') }}
          </p>

          <!-- Error block -->
          <div
            v-if="runHasError"
            class="rounded-lg bg-n-ruby-2 border border-n-ruby-5 p-3"
            role="alert"
          >
            <p class="text-xs font-semibold text-n-ruby-11 mb-1">
              {{ t('PIPELINE.FLOW_BUILDER.RUN.ERROR_LABEL') }}
            </p>
            <p
              v-if="runFailedNodeLabel"
              class="text-xs font-medium text-n-ruby-11 mb-1"
            >
              {{
                t('PIPELINE.FLOW_BUILDER.RUN.FAILED_NODE', {
                  node: runFailedNodeLabel,
                })
              }}
            </p>
            <pre
              class="text-xs text-n-ruby-11 font-mono whitespace-pre-wrap break-all leading-relaxed"
              >{{
                runErrorText || t('PIPELINE.FLOW_BUILDER.RUN.UNKNOWN_ERROR')
              }}</pre
            >
          </div>

          <!-- Steps -->
          <ol v-if="runSteps.length" class="space-y-2">
            <li
              v-for="(step, idx) in runSteps"
              :key="step.node_id || idx"
              class="flex items-start gap-3 rounded-lg border border-n-weak p-2.5"
            >
              <span
                class="flex items-center justify-center size-5 rounded-full bg-n-alpha-2 text-[10px] font-semibold text-n-slate-11 shrink-0 mt-0.5"
              >
                {{ step.order ?? idx + 1 }}
              </span>
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <span class="text-xs font-medium text-n-slate-12 truncate">
                    {{ step.label || step.node_type || step.node_id }}
                  </span>
                  <span
                    v-if="step.node_type"
                    class="text-[10px] px-1.5 py-0.5 rounded bg-n-alpha-1 text-n-slate-11 shrink-0"
                  >
                    {{ step.node_type }}
                  </span>
                </div>
                <p
                  v-if="
                    Array.isArray(step.would_execute) &&
                    step.would_execute.length
                  "
                  class="text-[11px] text-n-slate-11 mt-0.5"
                >
                  {{ step.would_execute.join(' · ') }}
                </p>
              </div>
              <span
                v-if="step.result"
                class="text-[10px] px-1.5 py-0.5 rounded shrink-0 mt-0.5"
                :class="
                  step.result === 'stop' || step.result === 'error'
                    ? 'bg-n-ruby-3 text-n-ruby-11'
                    : 'bg-n-teal-3 text-n-teal-11'
                "
              >
                {{ step.result }}
              </span>
            </li>
          </ol>

          <!-- Success without steps (execute happy path) -->
          <p
            v-else-if="!runHasError"
            class="text-sm text-n-slate-11 flex items-center gap-2"
          >
            <span
              class="i-lucide-check size-4 text-n-teal-9"
              aria-hidden="true"
            />
            {{ t('PIPELINE.FLOW_BUILDER.RUN.EXECUTE_SUCCESS') }}
          </p>
        </div>

        <!-- Footer -->
        <div
          class="px-5 py-3 border-t border-n-weak flex items-center justify-between flex-shrink-0"
        >
          <span class="text-xs text-n-slate-10">
            <template v-if="runSteps.length">
              {{
                t('PIPELINE.FLOW_BUILDER.RUN.STEPS_COUNT', {
                  count: runSteps.length,
                })
              }}
            </template>
          </span>
          <button
            type="button"
            class="min-h-11 px-4 py-2 rounded-md bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 transition-colors text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            @click="closeRunModal"
          >
            {{ t('PIPELINE.FLOW_BUILDER.RUN.CLOSE') }}
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>
